import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { finalize, Observable, tap } from 'rxjs';
import { IElmAssistSupportGroup } from '../../interfaces/employee-lifecycle/elm-assist-support-group.interface';
import { ElmAssistSupportScope } from '../../interfaces/employee-lifecycle/elm-assist-support-scope.enum';
import { IElmDistributionList } from '../../interfaces/employee-lifecycle/elm-distribution-list.interface';
import { ElmHardwareCategory } from '../../interfaces/employee-lifecycle/elm-hardware-category.enum';
import { IElmHardwareOptionCategory } from '../../interfaces/employee-lifecycle/elm-hardware-option-category.interface';
import { IElmHardwareOption } from '../../interfaces/employee-lifecycle/elm-hardware-option.interface';
import { IElmMailbox } from '../../interfaces/employee-lifecycle/elm-mailbox.interface';
import { IElmServiceLaneTaskType } from '../../interfaces/employee-lifecycle/elm-service-lane-task-type.interface';
import { IElmServiceLane } from '../../interfaces/employee-lifecycle/elm-service-lane.interface';
import { IElmMetadataStateModel } from '../../interfaces/employee-lifecycle/elm-metadata-state-model.interface';
import { EmployeeLifecycleMetadataService } from '../../services/employee-lifecycle-metadata.service';
import {
  LoadLifecycleAssistSupportGroups,
  LoadLifecycleDistributionLists,
  LoadLifecycleHardwareOptions,
  LoadLifecycleMailboxes,
  LoadLifecycleServiceLanes,
  LoadLifecycleServiceLaneTaskTypes,
} from './employee-lifecycle-metadata.actions';

const NEW_WORKFLOW_NOTEBOOK_OPTION_KEYS = new Set([
  'elmHardwareOptions.notebook.required',
  'elmHardwareOptions.notebook.notRequired',
]);

@State<IElmMetadataStateModel>({
  name: 'employeeLifecycleMetadata',
  defaults: {
    hardwareOptions: [],
    assistSupportGroups: [],
    mailboxes: [],
    distributionLists: [],
    serviceLanes: [],
    serviceLaneTaskTypes: [],
    isLoadingHardwareOptions: false,
    isLoadingAssistSupportGroups: false,
    isLoadingMailboxes: false,
    isLoadingDistributionLists: false,
    isLoadingServiceLanes: false,
    isLoadingServiceLaneTaskTypes: false,
  },
})
@Injectable()
export class EmployeeLifecycleMetadataState {
  private readonly metadataService = inject(EmployeeLifecycleMetadataService);

  @Selector()
  public static hardwareOptions(state: IElmMetadataStateModel): IElmHardwareOptionCategory[] {
    return state.hardwareOptions;
  }

  @Selector()
  public static notebookPreparationOptions(state: IElmMetadataStateModel): IElmHardwareOption[] {
    return this.getHardwareOptionsByCategory(state, ElmHardwareCategory.NotebookPreparation).filter(option =>
      NEW_WORKFLOW_NOTEBOOK_OPTION_KEYS.has(option.displayName),
    );
  }

  @Selector()
  public static mobilePhoneOptions(state: IElmMetadataStateModel): IElmHardwareOption[] {
    return this.getHardwareOptionsByCategory(state, ElmHardwareCategory.MobilePhone);
  }

  @Selector()
  public static mobilePhoneContractOptions(state: IElmMetadataStateModel): IElmHardwareOption[] {
    return this.getHardwareOptionsByCategory(state, ElmHardwareCategory.MobilePhoneContract);
  }

  @Selector()
  public static isLoadingHardwareOptions(state: IElmMetadataStateModel): boolean {
    return state.isLoadingHardwareOptions;
  }

  @Selector()
  public static assistSupportGroups(state: IElmMetadataStateModel): IElmAssistSupportGroup[] {
    return state.assistSupportGroups;
  }

  @Selector()
  public static preSalesAssistSupportGroups(state: IElmMetadataStateModel): IElmAssistSupportGroup[] {
    return this.getAssistSupportGroupsByScope(state, ElmAssistSupportScope.PreSales);
  }

  @Selector()
  public static afterSalesAssistSupportGroups(state: IElmMetadataStateModel): IElmAssistSupportGroup[] {
    return this.getAssistSupportGroupsByScope(state, ElmAssistSupportScope.AfterSales);
  }

  @Selector()
  public static isLoadingAssistSupportGroups(state: IElmMetadataStateModel): boolean {
    return state.isLoadingAssistSupportGroups;
  }

  @Selector()
  public static mailboxes(state: IElmMetadataStateModel): IElmMailbox[] {
    return state.mailboxes;
  }

  @Selector()
  public static isLoadingMailboxes(state: IElmMetadataStateModel): boolean {
    return state.isLoadingMailboxes;
  }

  @Selector()
  public static distributionLists(state: IElmMetadataStateModel): IElmDistributionList[] {
    return state.distributionLists;
  }

  @Selector()
  public static isLoadingDistributionLists(state: IElmMetadataStateModel): boolean {
    return state.isLoadingDistributionLists;
  }

  @Selector()
  public static serviceLanes(state: IElmMetadataStateModel): IElmServiceLane[] {
    return state.serviceLanes;
  }

  @Selector()
  public static isLoadingServiceLanes(state: IElmMetadataStateModel): boolean {
    return state.isLoadingServiceLanes;
  }

  @Selector()
  public static serviceLaneTaskTypes(state: IElmMetadataStateModel): IElmServiceLaneTaskType[] {
    return state.serviceLaneTaskTypes;
  }

  @Selector()
  public static isLoadingServiceLaneTaskTypes(state: IElmMetadataStateModel): boolean {
    return state.isLoadingServiceLaneTaskTypes;
  }

  @Action(LoadLifecycleHardwareOptions)
  public loadHardwareOptions(ctx: StateContext<IElmMetadataStateModel>): Observable<IElmHardwareOptionCategory[]> {
    ctx.patchState({ isLoadingHardwareOptions: true });
    return this.metadataService.getHardwareOptions().pipe(
      tap(hardwareOptions => ctx.patchState({ hardwareOptions })),
      finalize(() => ctx.patchState({ isLoadingHardwareOptions: false })),
    );
  }

  @Action(LoadLifecycleAssistSupportGroups)
  public loadAssistSupportGroups(ctx: StateContext<IElmMetadataStateModel>): Observable<IElmAssistSupportGroup[]> {
    ctx.patchState({ isLoadingAssistSupportGroups: true });
    return this.metadataService.getAssistSupportGroups().pipe(
      tap(assistSupportGroups => ctx.patchState({ assistSupportGroups })),
      finalize(() => ctx.patchState({ isLoadingAssistSupportGroups: false })),
    );
  }

  @Action(LoadLifecycleMailboxes)
  public loadMailboxes(ctx: StateContext<IElmMetadataStateModel>): Observable<IElmMailbox[]> {
    ctx.patchState({ isLoadingMailboxes: true });
    return this.metadataService.getMailboxes().pipe(
      tap(mailboxes => ctx.patchState({ mailboxes })),
      finalize(() => ctx.patchState({ isLoadingMailboxes: false })),
    );
  }

  @Action(LoadLifecycleDistributionLists)
  public loadDistributionLists(ctx: StateContext<IElmMetadataStateModel>): Observable<IElmDistributionList[]> {
    ctx.patchState({ isLoadingDistributionLists: true });
    return this.metadataService.getDistributionLists().pipe(
      tap(distributionLists => ctx.patchState({ distributionLists })),
      finalize(() => ctx.patchState({ isLoadingDistributionLists: false })),
    );
  }

  @Action(LoadLifecycleServiceLanes)
  public loadServiceLanes(ctx: StateContext<IElmMetadataStateModel>): Observable<IElmServiceLane[]> {
    ctx.patchState({ isLoadingServiceLanes: true });
    return this.metadataService.getServiceLanes().pipe(
      tap(serviceLanes => ctx.patchState({ serviceLanes })),
      finalize(() => ctx.patchState({ isLoadingServiceLanes: false })),
    );
  }

  @Action(LoadLifecycleServiceLaneTaskTypes)
  public loadServiceLaneTaskTypes(ctx: StateContext<IElmMetadataStateModel>): Observable<IElmServiceLaneTaskType[]> {
    ctx.patchState({ isLoadingServiceLaneTaskTypes: true });
    return this.metadataService.getServiceLaneTaskTypes().pipe(
      tap(serviceLaneTaskTypes => ctx.patchState({ serviceLaneTaskTypes })),
      finalize(() => ctx.patchState({ isLoadingServiceLaneTaskTypes: false })),
    );
  }

  private static getHardwareOptionsByCategory(state: IElmMetadataStateModel, category: ElmHardwareCategory): IElmHardwareOption[] {
    return state.hardwareOptions.find(group => group.category === category)?.options ?? [];
  }

  private static getAssistSupportGroupsByScope(state: IElmMetadataStateModel, scope: ElmAssistSupportScope): IElmAssistSupportGroup[] {
    return state.assistSupportGroups.filter(group => (group.supportScopes & scope) === scope);
  }
}
